/**
 *
 * debounces the function to be called once before or after specified interval
 *
 * @param {DebounceFunction} func - function to be debounced
 * @param {number} wait - time interval in milliseconds
 * @param {boolean} immediate - defines whether debounced function is called in the beginning or end of the interval
 * if true: The wrapped function is called immediately and then waits for the debounce interval before allowing further calls.
 * if false: The wrapped function is called after the debounce interval has elapsed since the last call to the wrapped function.
 *
 * Usage:
 * ```typescript
 *    const myFunction = () => {  };
 *    const [ myFunctionDebounced, clear ] = debounce(myFunction, 200);
 *    // calling the debounced function few times
 *    for (let i = 0; i < 3; i++) { myFunctionDebounced(); }
 *    // myFunction will be called only once, 200ms after the last call
 *
 *    clear(); //clears timeout to stop any remaining functions from calling
 * ```
 */
/**
 * @param  {DebounceFunction} func
 * @param  {number} wait
 * @param  {} immediate=false
 * @returns DebounceResult
 */
export function debounce(func, wait, immediate = false) {
  let timeout;
  const debouncedFunction = function (...args) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;
    const later = function () {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    };
    const callNow = immediate && !timeout;
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
    if (callNow) {
      func.apply(context, args);
    }
  };
  const clear = function () {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  return [debouncedFunction, clear];
}